/**
 * NPEContractDetailPage — chi tiết 1 NPE Contract.
 * Header (status chips + actions Edit/Copy/Print/Delete) +
 * Tabs (General Info | Fixations | Allocations).
 */
import { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import PrintIcon from '@mui/icons-material/Print'
import { useNavigate, useParams } from 'react-router-dom'

import ConfirmDialog from '@/components/ConfirmDialog'
import PageDataState from '@/components/PageDataState'
import RoleGuard from '@/components/RoleGuard'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useDeleteNPEContractMutation,
  useGetNPEContractQuery,
  useLazyPrintNPEContractQuery,
} from '@/store/api/npeContractsApi'
import type { NPEContractOut } from '@/types/api'
import { formatDate } from '@/utils/date'
import { getErrorMessage } from '@/utils/error'
import { formatNumber } from '@/utils/money'

import NPEContractFormDialog from './NPEContractFormDialog'
import NPEFixationListPage from './NPEFixationListPage'
import NPEAllocationListPage from './NPEAllocationListPage'

interface FieldRowProps {
  label: string
  value: React.ReactNode
}
function FieldRow({ label, value }: FieldRowProps) {
  return (
    <Box display="flex" gap={2} sx={{ flex: '1 1 280px', minWidth: 0 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 140, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {value === null || value === undefined || value === '' ? '—' : value}
      </Typography>
    </Box>
  )
}

interface SectionProps {
  title: string
  children: React.ReactNode
}
function Section({ title, children }: SectionProps) {
  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <Typography variant="subtitle1" fontWeight={700} color="primary">
        {title}
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={2}>
        {children}
      </Box>
      <Divider />
    </Box>
  )
}

function GeneralInfoTab({ d }: { d: NPEContractOut }) {
  return (
    <Box display="flex" flexDirection="column" gap={3} pt={2}>
      <Section title="General">
        <FieldRow label="NPE No" value={d.contract_no} />
        <FieldRow label="Contract Date" value={formatDate(d.contract_date)} />
        <FieldRow
          label="Vendor"
          value={`${d.cp_code ?? ''} — ${d.name ?? d.short_name ?? ''}`}
        />
        <FieldRow label="Quantity (kg)" value={formatNumber(d.quantity_kg, 0)} />
        <FieldRow label="Quality" value={d.quality_code} />
        <FieldRow label="Certification" value={d.certification} />
        <FieldRow label="Package" value={d.package_code} />
        <FieldRow label="Delivery Time" value={formatDate(d.delivery_time)} />
        <FieldRow label="Delivery Place" value={d.delivery_place_code} />
        <FieldRow label="Shipper" value={d.shipper_code} />
        <FieldRow label="Trader" value={d.trader_in_charge} />
      </Section>

      <Section title="Tracking">
        <FieldRow label="Received" value={formatNumber(d.received ?? null, 0)} />
        <FieldRow label="Fixed" value={formatNumber(d.fixed ?? null, 0)} />
        <FieldRow label="To be fixed" value={formatNumber(d.to_be_fix ?? null, 0)} />
        <FieldRow label="Paid" value={formatNumber(d.paid_vnd ?? null, 0)} />
      </Section>

      <Section title="Pricing & Storage">
        <FieldRow label="Provi Market Rate" value={d.provisional_market_rate} />
        <FieldRow label="Stop-loss Rate" value={d.stoploss_rate} />
        <FieldRow label="Interest Rate" value={d.interest_rate} />
        <FieldRow label="Storage Charge" value={formatNumber(d.storage_charge ?? null, 2)} />
        <FieldRow label="Storage Loss" value={formatNumber(d.storage_loss ?? null, 2)} />
        <FieldRow label="Return Quality" value={d.return_quality} />
        <FieldRow label="Return Package" value={d.return_package} />
        <FieldRow
          label="Two-way Handling Fee"
          value={formatNumber(d.two_way_handling_fee ?? null, 2)}
        />
        <FieldRow label="Insurance Fee" value={formatNumber(d.insurance_fee ?? null, 2)} />
      </Section>

      <Section title="Workflow flags">
        <FieldRow label="Signed by GM" value={d.is_signed_by_gm ? 'Yes' : 'No'} />
        <FieldRow label="Sent to Client" value={d.is_sent_to_client ? 'Yes' : 'No'} />
        <FieldRow label="Signed by Client" value={d.is_signed_by_client ? 'Yes' : 'No'} />
      </Section>

      <Section title="Notes">
        <FieldRow label="Note" value={d.note} />
        <FieldRow label="Interest Rate Note" value={d.interest_rate_note} />
      </Section>
    </Box>
  )
}

const TAB_LABELS = ['General Info', 'Fixations', 'Allocations']

export function NPEContractDetailPage() {
  const { canEdit } = usePagePermission()
  const { contractNo: rawContractNo = '' } = useParams<{ contractNo: string }>()
  const contractNo = decodeURIComponent(rawContractNo)
  const navigate = useNavigate()
  const snack = useSnackbar()

  const { data, isLoading, error, refetch } = useGetNPEContractQuery(contractNo, {
    skip: !contractNo,
  })
  const detail = data?.data

  const [tab, setTab] = useState(0)
  const [editOpen, setEditOpen] = useState(false)
  const [copyOpen, setCopyOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const [deleteCt, { isLoading: deleting }] = useDeleteNPEContractMutation()
  const [triggerPrint] = useLazyPrintNPEContractQuery()

  const statusChips = useMemo(() => {
    if (!detail) return null
    const chips: React.ReactNode[] = []
    if (detail.is_status === 0)
      chips.push(<Chip key="cancelled" size="small" label="Cancelled" />)
    else
      chips.push(<Chip key="active" size="small" label="Active" color="success" />)
    if (detail.is_signed_by_gm)
      chips.push(<Chip key="gm" size="small" label="Signed GM" color="info" />)
    if (detail.is_signed_by_client)
      chips.push(<Chip key="cl" size="small" label="Signed Client" color="info" />)
    return chips
  }, [detail])

  const handleCopy = () => {
    if (!detail) return
    setCopyOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!detail) return
    try {
      await deleteCt(detail.contract_no).unwrap()
      snack.success(`Deleted ${detail.contract_no}`)
      setConfirmDelete(false)
      navigate('/purchase/npe-contracts')
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete failed'))
    }
  }

  const handlePrint = async () => {
    if (!detail) return
    try {
      await triggerPrint(detail.contract_no).unwrap()
      snack.info('Print request sent')
    } catch (err) {
      snack.error(getErrorMessage(err, 'PDF print failed'))
    }
  }

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Tooltip title="Back">
          <IconButton onClick={() => navigate('/purchase/npe-contracts')}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h5" fontWeight={700}>
          {detail?.contract_no ?? contractNo}
        </Typography>
        {detail?.name || detail?.short_name ? (
          <Typography variant="subtitle1" color="text.secondary">
            — {detail?.name ?? detail?.short_name}
          </Typography>
        ) : null}
        <Stack direction="row" spacing={1}>
          {statusChips}
        </Stack>
        <Box flex={1} />
        {detail && (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              disabled={!canEdit}
              onClick={() => setEditOpen(true)}
            >
              Sửa
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              disabled={!canEdit}
              onClick={() => void handleCopy()}
            >
              Copy
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => void handlePrint()}
            >
              In PDF
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={!canEdit}
              onClick={() => setConfirmDelete(true)}
            >
              Xoá
            </Button>
          </Stack>
        )}
      </Box>

      <PageDataState loading={isLoading} error={error} empty={!isLoading && !detail}>
        {detail ? (
          <Paper sx={{ p: 2 }}>
            <Tabs
              value={tab}
              onChange={(_e, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {TAB_LABELS.map((l) => (
                <Tab key={l} label={l} />
              ))}
            </Tabs>
            <Box>
              {tab === 0 && <GeneralInfoTab d={detail} />}
              {tab === 1 && (
                <Box pt={2}>
                  <NPEFixationListPage embeddedContractNo={detail.contract_no} />
                </Box>
              )}
              {tab === 2 && (
                <Box pt={2}>
                  <NPEAllocationListPage embeddedNpeContract={detail.contract_no} />
                </Box>
              )}
            </Box>
          </Paper>
        ) : null}
      </PageDataState>

      <NPEContractFormDialog
        open={editOpen}
        mode="edit"
        initialValue={detail ?? null}
        onClose={() => setEditOpen(false)}
        onSaved={() => void refetch()}
      />

      <NPEContractFormDialog
        open={copyOpen}
        mode="copy"
        initialValue={detail ?? null}
        onClose={() => setCopyOpen(false)}
        onSaved={(_id) => {
          setCopyOpen(false)
        }}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete NPE Contract"
        message={`Are you sure you want to delete NPE Contract ${detail?.contract_no ?? ''}?`}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  )
}

export default NPEContractDetailPage
